import { ApplicationData } from './types'

/**
 * GB driver licence numbers encode the holder's names, date of birth and gender (e-Bulk specification 3.7.1).
 * The first 13 characters are computed from the application and must match what was typed; the DBS rejects a
 * mismatch and the specification says such an application must not be sent over e-Bulk until the identity details
 * are re-checked.
 */
export const computeDriverLicenceNumberPrefix = (value: ApplicationData, params: { convertMacToMc: boolean } = { convertMacToMc: false }): string => {
  const prefix: string[] = []

  const forename = (value.Forename ?? '').toUpperCase()
  const middlenames = (value.Middlenames?.Middlename ?? []).filter((m): m is string => typeof m === 'string' && m.length > 0)
  const numOfMiddlenames = middlenames.length
  const firstMiddlename = (middlenames[0] ?? '').toUpperCase()
  const surname = (value.PresentSurname ?? '').toUpperCase()
  const dob = (value.DateOfBirth ?? '0000-00-00').match(/^\d{4}-\d{2}-\d{2}$/)
    ? value.DateOfBirth ?? '0000-00-00'
    : '0000-00-00'
  const isFemale = value.Gender === 'female'

  // The first five characters must match the first five characters of the applicant's current surname. If the
  // surname is shorter than five characters the remainder is padded with 9s. Apostrophes can be omitted and "Mac"
  // can be shortened to "Mc" in the first three characters.
  const surnameWithoutApostrophes = surname.replace(/'/g, '')
  const surnameWithoutMac = surnameWithoutApostrophes.replace(/^MAC/, 'MC')
  const surnamePrefix = params.convertMacToMc
    ? surnameWithoutMac.substring(0, 5)
    : surnameWithoutApostrophes.substring(0, 5)
  const surnamePadded = surnamePrefix.padEnd(5, '9')
  prefix.splice(0, surnamePadded.length, ...surnamePadded.split(''))

  // The 6th and 11th characters, concatenated, are the rightmost two digits of the year of birth.
  prefix[5] = dob.substring(2, 3)
  prefix[10] = dob.substring(3, 4)

  // The 7th and 8th characters are the month of birth, with 5 added to the 7th for a female holder.
  prefix[6] = dob.substring(5, 6) === '0'
    ? isFemale ? '5' : '0'
    : isFemale ? '6' : '1'
  prefix[7] = dob.substring(6, 7)

  // The 9th and 10th characters are the day of birth.
  prefix[8] = dob.substring(8, 9)
  prefix[9] = dob.substring(9, 10)

  // The 12th character is the first letter of the forename.
  prefix[11] = forename.charAt(0) || 'x'

  // The 13th character is the first letter of the first middle name, or 9 when there is none.
  prefix[12] = numOfMiddlenames === 0
    ? '9'
    : firstMiddlename.charAt(0) || 'x'

  // No validation is applied to the 14th and subsequent characters.
  return prefix.join('')
}

export const isValidDriverLicenceNumber = (driverLicenceNumber: string, value: ApplicationData): 'yes' | 'no-too-long' | 'no-invalid-prefix' => {
  // Numbers shorter than 16 characters are not GB photocard/paper numbers in this format; nothing to check
  if (driverLicenceNumber.length < 16) return 'yes'
  if (driverLicenceNumber.length > 18) return 'no-too-long'

  const enteredPrefix = driverLicenceNumber.toUpperCase().substring(0, 13)
  if (enteredPrefix === computeDriverLicenceNumberPrefix(value)) return 'yes'
  if (enteredPrefix === computeDriverLicenceNumberPrefix(value, { convertMacToMc: true })) return 'yes'
  return 'no-invalid-prefix'
}
